import {
  useCallback,
  useMemo,
  useRef,
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect,
} from 'react';
import { store } from 'state/store';
import { useSelector, useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import {
  ScheduleComponent,
  Inject,
  ResourcesDirective,
  ResourceDirective,
  ViewsDirective,
  ViewDirective,
  TimelineViews,
  PopupCloseEventArgs,
  CellClickEventArgs,
  ResourceDetails,
  RenderCellEventArgs,
  Month,
  TimelineMonth,
  TimelineYear,
} from '@syncfusion/ej2-react-schedule';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import { Tag } from '@getsynapse/design-system';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Internationalization, setCulture } from '@syncfusion/ej2-base';
import { EventData, FetchedEvent } from 'utils/types/learningSchedule';
import {
  selectScheduleEvents,
  selectScheduleResources,
  selectEventsSearchTerm,
  fetchFacilitatorsEvents,
  selectFacilitatorsEvents,
  selectFacilitatorsResources,
  selectFacilitatorsViewSearchTerm,
  updateEventsCurrentView,
  updateFacilitatorsCurrentView,
  selectEventsCurrentView,
  selectFacilitatorsCurrentView,
  fetchEvents,
} from 'state/Schedule/ScheduleSlice';
import { transformEventForSchedule, localeMap } from '../../helpers/helpers';
import {
  headerTemplate,
  ContentTemplate,
  EventProps,
} from '../../components/EventQuickView/EventQuickView';
import 'Pages/LearningSchedule/components/Events/styles.css';
import {
  fetchFilterSettingByType,
  selectFiltersSettingsByType,
} from 'state/Settings/Filters/FiltersSlice';
import { SETTINGS_FILTERS, USER_STATUS } from 'utils/constants';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/ui';
import {
  UNASSIGNED_FACILITATOR,
  SCHEDULE_TABS,
} from 'utils/constants/learningSchedule';
import { useHolidayNavigation } from '../../hooks/useHolidayNavigation';
import {
  isUserOrganizationAdmin,
  selectUserId,
  isLDTeamManager,
  isUserFacilitator,
} from 'state/User/userSlice';
import { getCurrentLocale } from 'utils/localeHelper';
import { useEventColors } from './hooks/useEventColors';
import { getUserSummaries } from 'state/normalized/userSummaries/userSummariesSlice';
import { chunkArray } from 'Pages/helpers';
import {
  onAddingEvent,
  resetState,
  loadScheduleLocalization,
} from '../../utils';
import {
  useSchedulePopup,
  useScheduleActions,
  useDataForEventCreation,
} from '../../hooks';
import {
  FM_CONFIGURATION_OPTION_KEY,
  FM_CONFIGURATION_OPTION_VALUES,
} from 'utils/constants/fmSettingsModal';
import { selectUserViewSettingValues } from 'state/ViewSettings/viewSettingsSlice';

export interface EventsScheduleRef {
  onAddingEvent: () => void;
}
const currentLocale = getCurrentLocale() as keyof typeof localeMap;
const locale = localeMap[currentLocale] || 'en-US';

const ScheduleCalendar = forwardRef(
  ({ pageToLoad }: { pageToLoad: string }, ref) => {
    loadScheduleLocalization();
    const dispatch = useDispatch() as typeof store.dispatch;
    const isEventsPage = useMemo(
      () => pageToLoad === SCHEDULE_TABS.EVENTS,
      [pageToLoad]
    );
    const events = useSelector(selectScheduleEvents);
    const defaultResources = useSelector(selectScheduleResources);
    const facilitatorsResources = useSelector(selectFacilitatorsResources);
    const facilitatorsEvents = useSelector(selectFacilitatorsEvents);
    const filtersSettings = useSelector(
      selectFiltersSettingsByType(SETTINGS_FILTERS.SCHEDULE_EVENTS_VIEW)
    );
    const facilitatorsFilterSettings = useSelector(
      selectFiltersSettingsByType(SETTINGS_FILTERS.SCHEDULE_FACILITATORS_VIEW)
    );
    const isUserAdmin = useSelector(isUserOrganizationAdmin);
    const isTeamManager = useSelector(isLDTeamManager);
    const isFacilitator = useSelector(isUserFacilitator);
    const currentUserId = useSelector(selectUserId);
    const saveButtonRef = useRef<HTMLButtonElement | null>(null);
    const scheduleObj = useRef<any>(null);
    const search = useSelector(selectEventsSearchTerm);
    const facilitattorsViewSearch = useSelector(
      selectFacilitatorsViewSearchTerm
    );
    const currentView = useSelector(
      isEventsPage ? selectEventsCurrentView : selectFacilitatorsCurrentView
    );
    const { applyCategoryColor, eventsColors } = useEventColors();
    const {
      eventTimezones,
      fmShowWeekends,
      fmExpandedViews,
      fmVirtualScrolling,
      fmEventDescription = false,
    } = useFlags();
    const servicesToInject = useMemo(() => {
      if (isEventsPage) {
        return fmExpandedViews
          ? [TimelineViews, Month, TimelineMonth]
          : [TimelineViews, Month];
      }
      return fmExpandedViews
        ? [TimelineViews, Month, TimelineYear, TimelineMonth]
        : [TimelineViews, TimelineMonth];
    }, [isEventsPage, fmExpandedViews]);
    setCulture(locale);
    const [projectIds, setProjectIds] = useState<string[]>([]);
    const [eventTitle, setEventTitle] = useState<string>('');
    const [eventDescription, setEventDescription] = useState<string>('');
    const [eventType, setEventType] = useState<string>('');
    const [initialEvent, setInitialEvent] = useState<FetchedEvent | null>(null);
    const [updatedEvent, setUpdatedEvent] = useState<FetchedEvent | null>(null);
    const [isEditingSeriesWithExceptions, setIsEditingSeriesWithExceptions] =
      useState<boolean>(false);
    const [selectedFacilitatorsIds, setSelectedFacilitatorsIds] = useState<
      string[]
    >([]);
    const [eventsFiltersAreFetched, setEventsFiltersAreFetched] =
      useState<boolean>(false);
    const [facilitatorsFiltersAreFetched, setFacilitatorsFiltersAreFetched] =
      useState<boolean>(false);

    const deactivatedFacilitatorGroupIndexesSet = useMemo(() => {
      const deactivatedFacilitatorGroupIndexes = new Set<number>();
      facilitatorsResources.forEach((resource, index) => {
        if (
          resource.Status === USER_STATUS.REGISTERED_DISABLED ||
          resource.Status === USER_STATUS.INVITED_DISABLED
        ) {
          deactivatedFacilitatorGroupIndexes.add(index);
        }
      });
      return deactivatedFacilitatorGroupIndexes;
    }, [facilitatorsResources]);

    const eventsDataSource = useMemo(() => {
      const transformEvent = (event: EventData) => ({
        ...event,
        StartTime: new Date(event.StartTime),
        EndTime: new Date(event.EndTime),
      });
      const transformedEvents = eventTimezones
        ? events.map(transformEventForSchedule)
        : events.map(transformEvent);
      const transformedFacilitatorsEvents = eventTimezones
        ? facilitatorsEvents.map(transformEventForSchedule)
        : facilitatorsEvents.map(transformEvent);

      return isEventsPage ? transformedEvents : transformedFacilitatorsEvents; // Remove cloneDeep to avoid serialization issues
    }, [events, facilitatorsEvents, isEventsPage, eventTimezones]);

    const groupSource = useMemo(
      () => cloneDeep(isEventsPage ? defaultResources : facilitatorsResources),
      [facilitatorsResources, defaultResources, isEventsPage]
    );

    useEffect(() => {
      const fetchEventsSettings = async () => {
        await dispatch(
          fetchFilterSettingByType(SETTINGS_FILTERS.SCHEDULE_EVENTS_VIEW)
        );
        setEventsFiltersAreFetched(true);
      };

      if (!eventsFiltersAreFetched && pageToLoad === SCHEDULE_TABS.EVENTS) {
        fetchEventsSettings();
      }
    }, [dispatch, eventsFiltersAreFetched, pageToLoad]);

    useEffect(() => {
      const fetchFacilitatorsSettings = async () => {
        await dispatch(
          fetchFilterSettingByType(SETTINGS_FILTERS.SCHEDULE_FACILITATORS_VIEW)
        );
        setFacilitatorsFiltersAreFetched(true);
      };

      if (
        !facilitatorsFiltersAreFetched &&
        pageToLoad === SCHEDULE_TABS.FACILITATORS
      ) {
        fetchFacilitatorsSettings();
      }
    }, [dispatch, facilitatorsFiltersAreFetched, pageToLoad]);

    const renderNoRecords = useCallback(() => {
      if (
        !scheduleObj.current?.element ||
        !scheduleObj.current?.element.childNodes ||
        !scheduleObj.current?.element.childNodes?.length
      ) {
        return;
      }
      const noRecordsDiv = document.createElement('div');
      noRecordsDiv.textContent = intl.get('NO_RECORDS');

      scheduleObj.current?.element.childNodes[2].replaceWith(noRecordsDiv);
      for (let i = 0; i <= 2; i++) {
        (
          scheduleObj.current?.element.childNodes[0].childNodes[0].childNodes[0]
            .childNodes[0].childNodes[i].childNodes[0] as HTMLButtonElement
        ).disabled = true;
      }

      if (noRecordsDiv.style) {
        noRecordsDiv.style.display = 'flex';
        noRecordsDiv.style.justifyContent = 'center';
        noRecordsDiv.style.alignItems = 'center';
        noRecordsDiv.style.height = '100%';
        noRecordsDiv.style.fontSize = '0.875rem';
      }
    }, [scheduleObj]);

    const filters = useMemo(() => {
      if (!eventsFiltersAreFetched || !filtersSettings?.settings) return null;

      const { facilitators = [], ...restSettings } = filtersSettings?.settings;

      let unassignedSchedules = false;
      let updatedFacilitators = facilitators;

      if (facilitators.includes(UNASSIGNED_FACILITATOR)) {
        updatedFacilitators = facilitators.filter(
          (facilitator: string) => facilitator !== UNASSIGNED_FACILITATOR
        );
        unassignedSchedules = true;
      }

      return JSON.stringify({
        ...restSettings,
        facilitators: updatedFacilitators,
        unassignedSchedules,
      });
    }, [eventsFiltersAreFetched, filtersSettings]);

    const facilitatorsFilters = useMemo(() => {
      if (!facilitatorsFiltersAreFetched || !facilitatorsFilterSettings)
        return null;

      return JSON.stringify(facilitatorsFilterSettings.settings);
    }, [facilitatorsFiltersAreFetched, facilitatorsFilterSettings]);

    useEffect(() => {
      async function fetchEventsTabData() {
        const data = await dispatch(
          fetchEvents({
            search,
            filters: filters ? JSON.parse(filters! as string) : null,
          })
        );
        const matchedProjects = get(
          data,
          'payload.projectsMatchingSearchParams'
        );
        if (isEmpty(matchedProjects)) {
          renderNoRecords();
        } else {
          scheduleObj.current?.refresh();
        }
      }
      if (pageToLoad === SCHEDULE_TABS.EVENTS && eventsFiltersAreFetched) {
        fetchEventsTabData();
      }
    }, [
      dispatch,
      search,
      filters,
      eventsFiltersAreFetched,
      pageToLoad,
      scheduleObj,
      currentUserId,
      renderNoRecords,
    ]);

    const publicHolidaysViewEnabled = useSelector(
      selectUserViewSettingValues(FM_CONFIGURATION_OPTION_KEY)
    );

    const isPublicHolidaysViewEnabled = useMemo(() => {
      return (
        publicHolidaysViewEnabled?.value ===
        FM_CONFIGURATION_OPTION_VALUES.PUBLIC_HOLIDAYS_VIEW_ENABLED
      );
    }, [publicHolidaysViewEnabled?.value]);

    useEffect(() => {
      async function fetchFacilitatorsTabData() {
        // Get the current year from the calendar's selected date, or use current year as fallback
        const currentYear = scheduleObj.current?.selectedDate
          ? new Date(scheduleObj.current.selectedDate).getFullYear()
          : new Date().getFullYear();
        const data = await dispatch(
          fetchFacilitatorsEvents({
            search: facilitattorsViewSearch,
            filters: facilitatorsFilters
              ? JSON.parse(facilitatorsFilters! as string)
              : null,
            optionalParams: {
              showPublicHolidays: isPublicHolidaysViewEnabled,
              year: currentYear,
            },
          })
        );
        const matchedFacilitators = get(
          data,
          'payload.facilitatorsMatchingSearchParams'
        );
        if (isEmpty(matchedFacilitators)) {
          renderNoRecords();
        } else {
          scheduleObj.current?.refresh();
        }
      }
      if (
        pageToLoad === SCHEDULE_TABS.FACILITATORS &&
        facilitatorsFiltersAreFetched
      ) {
        fetchFacilitatorsTabData();
      }
    }, [
      dispatch,
      pageToLoad,
      facilitattorsViewSearch,
      facilitatorsFiltersAreFetched,
      scheduleObj,
      renderNoRecords,
      facilitatorsFilters,
      isPublicHolidaysViewEnabled,
    ]);

    useEffect(() => {
      const eventCreators = events
        .map((e) => e.createdBy)
        .filter((creator) => creator !== undefined) as string[];

      const facilitatorCreators = facilitatorsEvents
        .map((e) => e.createdBy)
        .filter((creator) => creator !== undefined) as string[];

      const allUserIds = [...eventCreators, ...facilitatorCreators];
      if (allUserIds.length === 0) return;

      const uniqueUserIds = [...new Set(allUserIds)];
      const chunks = chunkArray(uniqueUserIds, DEFAULT_PAGE_SIZE);

      chunks.forEach((chunk) => {
        dispatch(getUserSummaries({ userIds: chunk }));
      });
    }, [dispatch, events, facilitatorsEvents]);

    const onUpdateEventField = (key: string, value: any) => {
      setUpdatedEvent((prev) => {
        if (prev && prev.hasOwnProperty(key)) {
          return {
            ...prev,
            [key]: value,
          } as FetchedEvent;
        } else {
          return prev;
        }
      });
    };

    const resetStateHandler = () => {
      resetState({
        setProjectIds,
        setEventTitle,
        setEventDescription,
        setEventType,
        setSelectedFacilitatorsIds,
        setIsEditingSeriesWithExceptions,
      });
    };

    const intlInstance = useMemo(() => {
      return new Internationalization();
    }, []);

    const closeQuickInfo = useCallback(() => {
      if (
        scheduleObj &&
        scheduleObj.current &&
        scheduleObj.current.closeQuickInfoPopup
      ) {
        scheduleObj.current.closeQuickInfoPopup();
      }
    }, [scheduleObj]);

    const formatHeader = useCallback(
      (args: { date: Date }) => {
        const formattedDate = intlInstance.formatDate(args?.date, {
          skeleton: 'Ed',
        });
        return <span>{formattedDate}</span>;
      },
      [intlInstance]
    );
    useImperativeHandle(ref, () => ({
      onAddingEvent: () => onAddingEvent(undefined, scheduleObj),
    }));

    const oncancel = (args: PopupCloseEventArgs) => {
      if (args.type === 'Editor') {
        setUpdatedEvent(null);
        setInitialEvent(null);
        if (args.element.querySelector('.details-section')) {
          const detailsSection = args.element.querySelector('.details-section');
          detailsSection?.remove();
          const facilitatorsSection = args.element.querySelector(
            '.facilitators-section'
          );
          facilitatorsSection?.remove();
        }
      }
    };
    // Fetch data for filter side panel and filter pills
    useDataForEventCreation();

    const { actionBegin } = useScheduleActions({
      eventTimezones,
      projectIds,
      eventTitle,
      eventDescription,
      eventType,
      selectedFacilitatorsIds,
      isEditingSeriesWithExceptions,
      resetStateHandler,
      scheduleObj,
    });

    const { onPopupOpen } = useSchedulePopup({
      scheduleObj,
      eventTimezones,
      fmEventDescription,
      locale,
      events,
      facilitatorsEvents,
      setProjectIds,
      setEventTitle,
      setEventDescription,
      setEventType,
      setSelectedFacilitatorsIds,
      setIsEditingSeriesWithExceptions,
      setInitialEvent: (value: FetchedEvent | undefined) =>
        setInitialEvent(value || null),
      setUpdatedEvent: (value: FetchedEvent | undefined) =>
        setUpdatedEvent(value || null),
      onUpdateEventField,
      resetStateHandler,
      saveButtonRef,
      projectIds,
      eventTitle,
      eventType,
      initialEvent,
      updatedEvent,
    });

    const onCellClick = (args: CellClickEventArgs) => {
      args.cancel = true;
    };

    const onRenderCell = useCallback(
      (args: RenderCellEventArgs) => {
        if (
          args.groupIndex !== undefined &&
          !isEventsPage &&
          deactivatedFacilitatorGroupIndexesSet.has(args.groupIndex)
        ) {
          args.element.classList.add('e-disable');
        }
      },
      [deactivatedFacilitatorGroupIndexesSet, isEventsPage]
    );

    const resourceHeaderTemplate = useCallback(
      (props: ResourceDetails): JSX.Element => {
        if (isEventsPage) {
          return (
            <div className='text-sm truncate'>{props.resourceData.Title}</div>
          );
        } else {
          const status = props.resourceData?.Status;
          if (status && status !== USER_STATUS.REGISTERED) {
            const isPending = status === USER_STATUS.INVITED;
            return (
              <div className='flex flex-col py-1'>
                <div className='text-sm truncate'>
                  {props.resourceData.Name}
                </div>
                <Tag
                  label={intl.get(
                    `USERS_PAGE.TABLE.USER_STATUS.${status.toUpperCase()}`
                  )}
                  className={`border mt-0.5 border-transparent ${
                    isPending
                      ? 'bg-warning-lightest group-hover:border-warning-light'
                      : 'bg-neutral-lighter-two group-hover:border-neutral'
                  }`}
                  textClassName={
                    isPending ? 'text-warning-darker' : 'text-neutral-darker'
                  }
                />
              </div>
            );
          } else {
            return <div className='text-sm'>{props.resourceData.Name}</div>;
          }
        }
      },
      [isEventsPage]
    );

    const getCurrentView = () => {
      if (scheduleObj?.current?.currentView) {
        if (isEventsPage) {
          dispatch(
            updateEventsCurrentView({
              currentView: scheduleObj.current.currentView,
            })
          );
        } else {
          dispatch(
            updateFacilitatorsCurrentView({
              currentView: scheduleObj.current.currentView,
            })
          );
        }
      }
    };

    // Use shared holiday navigation hook
    const {
      handleNavigating,
      handleActionComplete,
      handleDataBound: handleHolidayDataBound,
    } = useHolidayNavigation({
      scheduleObj,
      pageToLoad,
      isPublicHolidaysViewEnabled,
      facilitattorsViewSearch,
      facilitatorsFilters,
      facilitatorsFiltersAreFetched,
      onDataBound: getCurrentView,
    });

    // Enhanced dataBound handler that combines view update and holiday refetch
    const handleDataBound = useCallback(() => {
      handleHolidayDataBound();
    }, [handleHolidayDataBound]);

    return (
      <div className='h-schedule overflow-y-auto' data-testid='schedule_div'>
        <ScheduleComponent
          timezone='UTC'
          locale={locale}
          height='100%'
          ref={scheduleObj}
          // CGP-207: showWeekend lives on the scheduler (not the
          // ViewDirectives) because view-level options are only read when a
          // view initializes — the top-level property reacts when
          // fmShowWeekends resolves after the post-login LD identify().
          // `?? false` preserves the flag-off fallback: Syncfusion's
          // Schedule.showWeekend default is `true`, so an undefined flag
          // would flash weekends on before LD resolves.
          showWeekend={fmShowWeekends ?? false}
          eventSettings={{
            dataSource: eventsDataSource,
            allowAdding: isUserAdmin || isTeamManager || isFacilitator,
            allowEditing: true,
            allowDeleting: true,
          }}
          actionBegin={actionBegin}
          actionComplete={handleActionComplete}
          actionFailure={actionBegin}
          popupClose={oncancel}
          navigating={handleNavigating}
          currentView={currentView}
          showTimeIndicator={false}
          cellClick={(args) => onCellClick(args)}
          rowAutoHeight={true}
          timeScale={{
            enable: false,
          }}
          renderCell={onRenderCell}
          dateHeaderTemplate={formatHeader}
          popupOpen={onPopupOpen}
          quickInfoTemplates={{
            header: headerTemplate(closeQuickInfo),
            content: (props: EventProps) =>
              ContentTemplate({ ...props, eventColor: eventsColors[props.Id] }),
          }}
          eventRendered={applyCategoryColor}
          resourceHeaderTemplate={resourceHeaderTemplate}
          dataBound={handleDataBound}
        >
          <ViewsDirective>
            <ViewDirective
              option='TimelineWeek'
              group={{
                resources: [isEventsPage ? 'projects' : 'facilitators'],
              }}
              allowVirtualScrolling={fmVirtualScrolling}
              displayName='Week'
            />
            {fmExpandedViews && (
              <ViewDirective
                option='TimelineMonth'
                allowVirtualScrolling={fmVirtualScrolling}
                displayName='Month'
                orientation='Vertical'
                group={{
                  resources: [isEventsPage ? 'projects' : 'facilitators'],
                }}
              />
            )}
            <ViewDirective option='Month' displayName='Monthly Calendar' />
            {fmExpandedViews && !isEventsPage && (
              <ViewDirective
                option='TimelineYear'
                allowVirtualScrolling={fmVirtualScrolling}
                displayName='Year'
                orientation='Vertical'
                {...(!isEventsPage && {
                  group: {
                    resources: ['facilitators'],
                  },
                })}
              />
            )}
          </ViewsDirective>
          <Inject services={servicesToInject} />
          <ResourcesDirective>
            <ResourceDirective
              name={isEventsPage ? 'projects' : 'facilitators'}
              dataSource={groupSource}
              textField={isEventsPage ? 'Title' : 'Name'}
              field={isEventsPage ? 'ProjectId' : 'FacilitatorId'}
              idField={isEventsPage ? 'ProjectId' : 'FacilitatorId'}
            />
          </ResourcesDirective>
        </ScheduleComponent>
      </div>
    );
  }
);

export default ScheduleCalendar;
